/**
 * One of the owner's own Agents, side by side with another (Hub `#/parallel`).
 *
 * Each mounted Agent uses its existing `/agents/<id>/` routes, so it owns its
 * own API prefix, its own subscription and its own identity: nothing here
 * reaches across from one Agent to the next, and that is the whole point of the
 * page. Docking keeps the frame alive with its documents and unsent words;
 * hiding it pauses media and status polling, because an embedded workspace
 * closes its live subscription when it is told it cannot be seen.
 *
 * It used to live in a developer page (`studio/WallPage.tsx`) that drew a board
 * of delegate tiles around it, on a route an Owner never had a way to reach:
 * `#/wall` rendered 工作空间 for an Owner and the board for a developer, so it
 * was a second address for one page and a page nothing led to, at once (D656).
 * The board went; this stayed, because 并排工作 leads here.
 */

import { useEffect, useState } from "react";
import type { HubAgent } from "../lib/api";
import { rememberedWorkHref } from "../connections/navigation";
import { useAgentFrame } from "./agentFrame";
import { instanceStatus, wakesOnDemand } from "./api";

export function IndependentAgent({
  agent,
  hidden,
  onDock,
}: {
  agent: HubAgent;
  hidden: boolean;
  onDock: () => void;
}) {
  const scope = `/agents/${encodeURIComponent(agent.id)}`;
  const [initialWork] = useState(() => rememberedWorkHref(scope) || "#/workspace");
  const available = agent.online || wakesOnDemand(agent);
  const [opened, setOpened] = useState(!hidden && available);
  useEffect(() => { if (!hidden && available) setOpened(true); }, [hidden, available]);
  const frame = useAgentFrame({ visible: !hidden && available, agent });
  return (
    <article
      className="independent-agent"
      style={{ display: hidden ? "none" : undefined }}
    >
      <header className="independent-agent-head">
        <div className="agent-affiliation">
          <h2>{agent.name || agent.id}</h2>
          <p>
            你的 IP · {instanceStatus(agent).label}
          </p>
        </div>
        <a
          className="btn"
          href={scope + "/" + initialWork}
          onClick={event => {
            const work = rememberedWorkHref(scope) || frame.ref.current?.contentWindow?.location.hash || initialWork;
            event.currentTarget.href = scope + "/" + work;
          }}
        >
          展开
        </a>
        <button
          type="button"
          className="btn btn-quiet"
          onClick={onDock}
          aria-label={`收起 ${agent.name || agent.id}`}
        >
          收起
        </button>
      </header>
      {!available && opened ? <p className="agent-offline" role="status">连接暂时中断，界面先留着，恢复后可以接着用。</p> : null}
      {opened ? (
        <iframe
          ref={frame.ref}
          title={(agent.name || agent.id) + " 的工作区"}
          src={
            scope + "/?workspace=embedded" + initialWork
          }
          onLoad={frame.onLoad}
        />
      ) : (
        <p className="agent-offline">
          这个 IP 目前离线。恢复连接后可继续，其他 IP 的工作不受影响。
        </p>
      )}
    </article>
  );
}
